<template>
  <!-- Cilindro (caixa redonda) -->
  <svg
    v-if="redonda"
    class="wt-svg"
    :viewBox="`0 0 ${W} ${H}`"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    :aria-label="ariaLabel"
  >
    <defs>
      <!-- Recorta a água ao corpo do cilindro, para ela nunca vazar pelas bordas. -->
      <clipPath :id="`wt-clip-${uid}`">
        <path :d="corpoCilindro" />
      </clipPath>
      <linearGradient :id="`wt-grad-${uid}`" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" :stop-color="AGUA_TOPO" />
        <stop offset="100%" :stop-color="AGUA_FUNDO" />
      </linearGradient>
    </defs>

    <!-- Água: só aparece quando há nível. Sobe/desce conforme `percent`. -->
    <g v-if="pct > 0" :clip-path="`url(#wt-clip-${uid})`">
      <rect :x="0" :y="nivelY" :width="W" :height="H - nivelY" :fill="`url(#wt-grad-${uid})`" />
      <!-- Elipse da superfície: dá a perspectiva do líquido visto de cima. -->
      <ellipse :cx="cx" :cy="nivelY" :rx="rx" :ry="ry" :fill="AGUA_SUP" />
    </g>

    <!-- Fundo e corpo do tanque (contorno por cima da água). -->
    <ellipse :cx="cx" :cy="baseY" :rx="rx" :ry="ry" fill="none" :stroke="TRACO" :stroke-width="SW" />
    <path :d="lateraisCilindro" fill="none" :stroke="TRACO" :stroke-width="SW" />
    <!-- Boca do tanque. -->
    <ellipse :cx="cx" :cy="topoY" :rx="rx" :ry="ry" fill="none" :stroke="TRACO" :stroke-width="SW" />
    <!-- Linha tracejada: parte de trás do fundo, vista através do tanque. -->
    <path :d="fundoOculto" fill="none" :stroke="TRACO_OCULTO" :stroke-width="SW" stroke-dasharray="4 3" />
  </svg>

  <!-- Prisma (caixa quadrada), em projeção isométrica simples -->
  <svg
    v-else
    class="wt-svg"
    :viewBox="`0 0 ${W} ${H}`"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    :aria-label="ariaLabel"
  >
    <defs>
      <linearGradient :id="`wt-grad-${uid}`" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" :stop-color="AGUA_TOPO" />
        <stop offset="100%" :stop-color="AGUA_FUNDO" />
      </linearGradient>
    </defs>

    <g v-if="pct > 0">
      <!-- Corpo da água: faces frontal e lateral, seguindo a mesma inclinação do prisma. -->
      <path :d="corpoAguaPrisma" :fill="`url(#wt-grad-${uid})`" />
      <!-- Losango da superfície: mesma inclinação das faces, para casar a perspectiva. -->
      <path :d="supPrisma" :fill="AGUA_SUP" />
    </g>

    <!-- Arestas visíveis. -->
    <path :d="arestasPrisma" fill="none" :stroke="TRACO" :stroke-width="SW" stroke-linejoin="round" />
    <!-- Arestas ocultas (o canto de trás), tracejadas. -->
    <path :d="arestasOcultas" fill="none" :stroke="TRACO_OCULTO" :stroke-width="SW" stroke-dasharray="4 3" />
  </svg>
</template>

<script>
// Desenha o reservatório com o nível de água proporcional ao dado real.
//
// Os ícones water-tank-rouded.svg / water-tank-square.svg são PNG rasterizado com a
// água num nível fixo, então não servem para representar o nível. Este componente
// redesenha as duas formas em SVG vetorial seguindo o mesmo visual (contorno verde,
// água lilás, arestas ocultas tracejadas), com a água numa camada própria.
let _uid = 0;

export default {
  name: "WaterTank",
  props: {
    // 0..100 — quanto do reservatório está cheio.
    percent: { type: Number, default: 0 },
    // "REDONDA" | "QUADRADA"
    formato: { type: String, default: "REDONDA" },
  },
  data() {
    return {
      uid: ++_uid,
      W: 130,
      H: 100,
      SW: 1.2,
      TRACO: "#4A7C2F",
      TRACO_OCULTO: "#8AA57B",
      AGUA_TOPO: "#C9C5F2",
      AGUA_FUNDO: "#DCDAF7",
      AGUA_SUP: "#B9B4EC",
    };
  },
  computed: {
    redonda() {
      return this.formato !== "QUADRADA";
    },
    ariaLabel() {
      const forma = this.redonda ? "redonda" : "quadrada";
      return `Caixa d'água ${forma} com ${Math.round(this.pct)}% de capacidade`;
    },
    // Percentual limitado a 0..100 — o sensor pode reportar acima da altura.
    // Percentual saneado: 0..100, com NaN/Infinity/negativo caindo para 0.
    // É o único valor usado pelo desenho — nunca usar `percent` cru no template,
    // senão uma leitura com falha (NaN, negativo) escapa do limite e a água
    // aparece fora do tanque.
    pct() {
      const n = Number(this.percent);
      if (!Number.isFinite(n)) return 0;
      return Math.max(0, Math.min(100, n));
    },

    // --- Geometria do cilindro ---
    cx() {
      return this.W / 2;
    },
    rx() {
      return this.W / 2 - 8;
    },
    ry() {
      return 13;
    },
    topoY() {
      return 16;
    },
    baseY() {
      return this.H - 16;
    },
    // Altura da coluna d'água, medida entre os centros das elipses.
    nivelY() {
      return this.baseY - (this.baseY - this.topoY) * (this.pct / 100);
    },
    corpoCilindro() {
      const { cx, rx, ry, topoY, baseY, W } = this;
      return [
        `M ${cx - rx},${topoY}`,
        `A ${rx},${ry} 0 0 0 ${cx + rx},${topoY}`,
        `L ${cx + rx},${baseY}`,
        `A ${rx},${ry} 0 0 1 ${cx - rx},${baseY}`,
        `Z`,
      ].join(" ") + ` M 0,0 M ${W},0`;
    },
    lateraisCilindro() {
      const { cx, rx, topoY, baseY, ry } = this;
      return [
        `M ${cx - rx},${topoY} L ${cx - rx},${baseY}`,
        `M ${cx + rx},${topoY} L ${cx + rx},${baseY}`,
        `M ${cx - rx},${baseY} A ${rx},${ry} 0 0 0 ${cx + rx},${baseY}`,
      ].join(" ");
    },
    fundoOculto() {
      const { cx, rx, ry, baseY } = this;
      return `M ${cx - rx},${baseY} A ${rx},${ry} 0 0 1 ${cx + rx},${baseY}`;
    },

    // --- Geometria do prisma (isométrica) ---
    // dx/dy = deslocamento da face de trás, dando a sensação de profundidade.
    px() {
      return { x0: 10, x1: 96, dx: 24, dy: -18, yTopo: 30, yBase: this.H - 12 };
    },
    nivelYPrisma() {
      const { yTopo, yBase } = this.px;
      return yBase - (yBase - yTopo) * (this.pct / 100);
    },
    // Sólido da água: face frontal (x0..x1) + face lateral direita, ambas na
    // perspectiva do prisma. Desenhar um <rect> reto aqui faria a água "vazar"
    // pela lateral, já que a face de trás é deslocada por dx/dy.
    corpoAguaPrisma() {
      const { x0, x1, dx, dy, yBase } = this.px;
      const y = this.nivelYPrisma;
      return [
        // frente
        `M ${x0},${y} L ${x1},${y} L ${x1},${yBase} L ${x0},${yBase} Z`,
        // lateral direita (para trás e para cima)
        `M ${x1},${y} L ${x1 + dx},${y + dy} L ${x1 + dx},${yBase + dy} L ${x1},${yBase} Z`,
      ].join(" ");
    },
    supPrisma() {
      const { x0, x1, dx, dy } = this.px;
      const y = this.nivelYPrisma;
      return [
        `M ${x0},${y}`,
        `L ${x0 + dx},${y + dy}`,
        `L ${x1 + dx},${y + dy}`,
        `L ${x1},${y}`,
        `Z`,
      ].join(" ");
    },
    arestasPrisma() {
      const { x0, x1, dx, dy, yTopo, yBase } = this.px;
      return [
        // face frontal
        `M ${x0},${yTopo} L ${x1},${yTopo} L ${x1},${yBase} L ${x0},${yBase} Z`,
        // topo
        `M ${x0},${yTopo} L ${x0 + dx},${yTopo + dy} L ${x1 + dx},${yTopo + dy} L ${x1},${yTopo}`,
        // lateral direita
        `M ${x1},${yBase} L ${x1 + dx},${yBase + dy} L ${x1 + dx},${yTopo + dy}`,
      ].join(" ");
    },
    arestasOcultas() {
      const { x0, x1, dx, dy, yTopo, yBase } = this.px;
      // Canto de trás-esquerda, que só se vê "através" do tanque.
      return [
        `M ${x0},${yBase} L ${x0 + dx},${yBase + dy}`,
        `M ${x0 + dx},${yBase + dy} L ${x1 + dx},${yBase + dy}`,
        `M ${x0 + dx},${yBase + dy} L ${x0 + dx},${yTopo + dy}`,
      ].join(" ");
    },
  },
};
</script>

<style scoped>
.wt-svg {
  display: block;
  width: 100%;
  max-width: 150px;
  height: auto;
  margin: 0 auto;
}
</style>
